import styled from "@emotion/styled";
import OktaSignIn from "@okta/okta-signin-widget";
import React, { useEffect, useRef } from "react";
import {
  oktaExternalAuthConfig,
  oktaInternalAuthConfig,
} from "../lib/auth-config";

const isInternalEmail = (email: string) => {
  const split = email.split("@");
  const domain = split[1];

  return domain === "limaonecapital.com";
};

const getLoginConfig = (isInternal: Boolean) => {
  return isInternal ? oktaInternalAuthConfig : oktaExternalAuthConfig;
};

function OktaSignInWidget({ config, onSuccess, onError }: any) {
  const [oktaConfig, setConfig] = React.useState<any>(config);
  const [input, setInput] = React.useState("");

  const widgetRef = useRef<any>();

  useEffect(() => {
    if (!widgetRef.current) return;

    const widget = new OktaSignIn(oktaConfig);

    widget
      .showSignInToGetTokens({
        el: widgetRef.current,
      })
      .then(onSuccess)
      .catch(onError);

    widget.on("ready", (context: any) => {
      const username = document.getElementById(
        "okta-signin-username"
      ) as HTMLInputElement;

      if (!username) {
        return;
      }

      username.addEventListener("change", (e: any) => {
        setInput(e.target.value);
      });
    });

    return () => widget.remove();
  }, [oktaConfig, onSuccess, onError]);

  useEffect(() => {
    if (!input) return;

    const isInternal = isInternalEmail(input);
    setConfig(getLoginConfig(isInternal));
  }, [input]);

  return (
    <SignInWidgetWrapper>
      <div ref={widgetRef} />
    </SignInWidgetWrapper>
  );
}

export default OktaSignInWidget;

const SignInWidgetWrapper = styled.div`
  width: 100%;

  #okta-sign-in {
    .focused-input {
      box-shadow: none;
    }

    .o-form-theme {
      padding: 0;
    }

    .auth-footer {
      .link {
        color: var(--chakra-colors-brand-400);
        font-weight: 500;

        &:hover {
          color: var(--chakra-colors-brand-400);
        }

        &:active {
          color: var(--chakra-colors-brand-300);
        }

        &:focus {
          box-shadow: none;
        }
      }
    }

    &.auth-container {
      &.main-container {
        background-color: white;
        border: none;
        padding: var(--chakra-sizes-8) var(--chakra-sizes-4);
        margin-top: var(--chakra-sizes-8);
        font-family: "Roboto";
        box-shadow: var(--chakra-shadows-sm);
        border: 1px solid var(--chakra-colors-gray-200);
        color: var(--chakra-colors-gray-700);
        max-width: 500px;
        width: 100%;
        border-radius: var(--chakra-radii-md);

        .okta-sign-in-header {
          &.auth-header {
            display: none;
          }
        }
      }

      input[type="submit"] {
        font-family: "Roboto", sans-serif;
        background: var(--chakra-colors-brand-500);
        font-size: var(--chakra-fontSizes-md);
        border-radius: var(--chakra-radii-md);
        font-weight: 500;
        padding-inline-start: var(--chakra-space-4);
        padding-inline-end: var(--chakra-space-4);
        height: var(--chakra-sizes-12);
        letter-spacing: 0.5px;

        &:hover {
          background: var(--chakra-colors-brand-400);
        }

        &:active {
          background: var(--chakra-colors-brand-300);
        }

        &:focus {
          box-shadow: none;
          border-color: unset;
        }
      }

      .o-form-head {
        display: none;
      }

      .o-form-label {
        letter-spacing: 0.25px;

        label {
          font-size: var(--chakra-fontSizes-sm);
          color: var(--chakra-colors-gray-600);
          font-weight: 500;
        }
      }

      .o-form-fieldset {
        margin-bottom: var(--chakra-sizes-6);
      }

      .o-form-input-error {
        font-size: var(--chakra-fontSizes-xs);
        letter-spacing: 0.25px;
        padding-left: 0;

        .icon-16 {
          display: none;
        }
      }

      .okta-form-input-field {
        border-color: var(--chakra-colors-gray-300);
        border-radius: var(--chakra-radii-md);
        height: var(--chakra-sizes-10);

        input {
          font-family: "Roboto", "sans-serif";
          border-radius: var(--chakra-radii-md);
          padding-inline-start: var(--chakra-space-4);
          padding-inline-end: var(--chakra-space-4);

          &:hover {
            border-color: var(--chakra-colors-gray-400);
          }

          &:focus {
            z-index: 1;
            border-color: rgb(49, 130, 206);
            box-shadow: rgb(49 130 206) 0px 0px 0px 1px;
          }
        }
      }
    }
  }
`;
